// Web-exclusive Expiration Date selector for Post Job
// Mirrors native ExpirationDateSelect: 30/60/90-day presets + "Choose Custom Date" calendar.

import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, Pressable } from 'react-native';
import { Calendar, ChevronUp, ChevronDown } from 'lucide-react';
import DateTimePicker from 'react-native-ui-datepicker';
import { webColors, webBorderRadius, webShadows } from '../../constants/webStyles';

const DAY_MS = 24 * 60 * 60 * 1000;

const PRESET_OPTIONS = [
  { days: 30, label: '30 days' },
  { days: 60, label: '60 days' },
  { days: 90, label: '90 days' },
];

// Format a Date as e.g. "07 Jul 2026" (matches existing web format)
const formatDate = (date) =>
  date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });

// Calendar theme to match design system (shared with WebDateTimePicker look)
const pickerStyles = {
  calendarContainer: { backgroundColor: webColors.cardBackground },
  monthSelector: {
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  yearSelector: {
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  selected: {
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.small,
  },
  selected_label: { color: '#ffffff', fontWeight: '600' },
  today: {
    borderWidth: 2,
    borderColor: webColors.primary,
    borderRadius: webBorderRadius.small,
  },
  today_label: { color: webColors.primary, fontWeight: '600' },
  day: { borderRadius: webBorderRadius.small },
  day_label: { color: webColors.textPrimary, fontSize: 14 },
  weekday: { color: webColors.textMuted, fontSize: 12, fontWeight: '500' },
  disabled: { opacity: 0.3 },
  disabled_label: { color: webColors.textMuted },
  outside: { opacity: 0.4 },
  outside_label: { color: webColors.textMuted },
  header: { backgroundColor: webColors.cardBackground, paddingVertical: 12 },
  button_prev: {
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
    padding: 8,
  },
  button_next: {
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
    padding: 8,
  },
};

const WebExpirationDateSelect = ({ value, onChange, placeholder = 'Select Expiration Date', error }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [tempDate, setTempDate] = useState(null);

  // Parse incoming value (Date | ISO string | null) — derived, no local mirror state
  const parsedValue = useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }, [value]);

  // Determine which preset (if any) the current value matches (±1 day tolerance)
  const matchedPreset = useMemo(() => {
    if (!parsedValue) return null;
    const daysDiff = Math.round((parsedValue.getTime() - Date.now()) / DAY_MS);
    return PRESET_OPTIONS.find((opt) => Math.abs(daysDiff - opt.days) <= 1) || null;
  }, [parsedValue]);

  const getDisplayText = () => {
    if (!parsedValue) return placeholder;
    if (matchedPreset) return `${matchedPreset.label} (${formatDate(parsedValue)})`;
    return formatDate(parsedValue);
  };

  // Bounds for custom date: tomorrow → +1 year
  const minDate = useMemo(() => new Date(Date.now() + DAY_MS), []);
  const maxDate = useMemo(() => new Date(Date.now() + 365 * DAY_MS), []);

  const handlePresetSelect = (opt) => {
    onChange(new Date(Date.now() + opt.days * DAY_MS));
    setShowOptions(false);
  };

  const handleChooseCustom = () => {
    setShowOptions(false);
    setTempDate(parsedValue || minDate);
    setShowCustomPicker(true);
  };

  const handleConfirmCustom = () => {
    if (tempDate) onChange(tempDate instanceof Date ? tempDate : new Date(tempDate));
    setShowCustomPicker(false);
  };

  const handleCancelCustom = () => {
    setTempDate(null);
    setShowCustomPicker(false);
  };

  return (
    <View>
      {/* Trigger */}
      <TouchableOpacity
        style={[styles.selectDropdown, error && styles.selectDropdownError]}
        onPress={() => setShowOptions(!showOptions)}
        activeOpacity={0.7}
      >
        <View style={styles.selectDropdownContent}>
          <Calendar size={16} color={webColors.textPrimary} />
          <Text style={[styles.selectDropdownText, !parsedValue && styles.selectDropdownPlaceholder]}>
            {getDisplayText()}
          </Text>
        </View>
        <View style={styles.selectDropdownArrow}>
          {showOptions ? (
            <ChevronUp size={16} color={webColors.textMuted} />
          ) : (
            <ChevronDown size={16} color={webColors.textMuted} />
          )}
        </View>
      </TouchableOpacity>

      {/* Preset options */}
      {showOptions && (
        <View style={styles.expirationOptions}>
          {PRESET_OPTIONS.map((opt) => {
            const isSelected = matchedPreset?.days === opt.days;
            return (
              <TouchableOpacity
                key={opt.days}
                style={[styles.expirationOption, isSelected && styles.expirationOptionSelected]}
                onPress={() => handlePresetSelect(opt)}
              >
                <Text style={[styles.expirationDays, isSelected && styles.expirationDaysSelected]}>
                  {opt.label}
                </Text>
                <Text style={styles.expirationDate}>
                  {formatDate(new Date(Date.now() + opt.days * DAY_MS))}
                </Text>
              </TouchableOpacity>
            );
          })}

          {/* Choose Custom Date */}
          <TouchableOpacity
            style={[styles.expirationOption, styles.customOption]}
            onPress={handleChooseCustom}
          >
            <View style={styles.customOptionContent}>
              <Calendar size={16} color={webColors.primary} />
              <Text style={styles.customOptionText}>Choose Custom Date</Text>
            </View>
          </TouchableOpacity>
        </View>
      )}

      {/* Custom date calendar modal */}
      <Modal
        visible={showCustomPicker}
        transparent
        animationType="fade"
        onRequestClose={handleCancelCustom}
      >
        <Pressable style={styles.overlay} onPress={handleCancelCustom}>
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Expiration Date</Text>
            </View>

            <View style={styles.pickerContainer}>
              <DateTimePicker
                mode="single"
                date={tempDate}
                onChange={({ date }) => setTempDate(date ? new Date(date) : null)}
                minDate={minDate}
                maxDate={maxDate}
                styles={pickerStyles}
                locale="en"
                firstDayOfWeek={0}
              />
            </View>

            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelButton} onPress={handleCancelCustom}>
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.confirmButton} onPress={handleConfirmCustom}>
                <Text style={styles.confirmButtonText}>Confirm</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  // Trigger (matches StepJobDetails dropdown)
  selectDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    padding: 14,
    paddingHorizontal: 16,
  },
  selectDropdownError: {
    borderColor: webColors.error,
  },
  selectDropdownContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  selectDropdownText: {
    fontSize: 14,
    color: webColors.textPrimary,
  },
  selectDropdownPlaceholder: {
    color: webColors.textMuted,
  },
  selectDropdownArrow: {},
  // Options list
  expirationOptions: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    marginTop: 8,
    overflow: 'hidden',
  },
  expirationOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  expirationOptionSelected: {
    backgroundColor: webColors.primaryLight,
  },
  expirationDays: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textPrimary,
  },
  expirationDaysSelected: {
    color: webColors.primary,
    fontWeight: '600',
  },
  expirationDate: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  customOption: {
    borderBottomWidth: 0,
  },
  customOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  customOptionText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.primary,
  },
  // Modal chrome (matches WebDateTimePicker)
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
    ...webShadows.modal,
  },
  modalHeader: {
    padding: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    textAlign: 'center',
  },
  pickerContainer: {
    padding: 16,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: webColors.borderLight,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  confirmButton: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  confirmButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
});

export default WebExpirationDateSelect;
